"use client"

import React, { useEffect, useState } from "react"
import {
  Send,
  Calendar,
  Clock,
  ThumbsUp,
  MessageCircle,
  Megaphone,
  ChevronDown,
  ChevronUp,
  BookOpen,
  FileText,
  GraduationCap,
  Paperclip,
  Plus,
  Trash2,
  Loader2,
  X,
} from "lucide-react"
import { postAws } from "../postAws"
import { getAnnouncements } from "./api"
import { postTeacherAnnouncementFile, generatePresignedUrl } from "../postFileAws"

const announcement_topics = [
  { value: "general", label: "General", icon: Megaphone },
]

export default function AnnouncementComponent() {
  const [posts, setPosts] = useState([])
  const [announcement_topic, setAnnouncementTopic] = useState("general")
  const [announcementDescription, setAnnouncementDescription] = useState("")
  const [currentUser, setCurrentUser] = useState({})
  const [announcementAttachment, setAnnouncementAttachment] = useState(null)
  const [preSignedUrls, setPreSignedUrls] = useState(false)
  const [newComments, setNewComments] = useState({})
  const [classData, setClassData] = useState(null)
  const [teacherData, setTeacherData] = useState()
  const [expandedComments, setExpandedComments] = useState(new Set())
  const [isCreateAnnouncementOpen, setIsCreateAnnouncementOpen] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [isPageLoading, setIsPageLoading] = useState(true)
  const [activeAttachment, setActiveAttachment] = useState(null)

  const handleDisplayAttachment = (postId) => {
    setPosts(
      posts.map((post) =>
        post._id === postId && post.isDisplayAttachment
          ? {
              ...post,
              isDisplayAttachment: false,
            }
          : post._id === postId && !post.isDisplayAttachment
            ? {
                ...post,
                isDisplayAttachment: true,
              }
            : post,
      ),
    )
  }

  // Fetch class and teacher data from localStorage
  useEffect(() => {
    if (!classData) {
      const classStr = localStorage.getItem("classData")
      setClassData(JSON.parse(classStr))
    }
    if (!teacherData) {
      const teacherStr = localStorage.getItem("teacherData")
      setTeacherData(JSON.parse(teacherStr))
    }
  }, [classData, teacherData])

  async function processAnnouncements(announcements) {
    const processedAnnouncements = await Promise.all(
      announcements.map(async (announcement) => {
        if (announcement.announcementAttachment !== "") {
          announcement.attachmentPresignedUrl = await generatePresignedUrl(announcement.announcementAttachment)
          const fileName = announcement.announcementAttachment.split("/").pop()
          announcement.attachmentName = fileName
          announcement.isDisplayAttachment = false
        }
        return announcement
      }),
    )
    return processedAnnouncements
  }

  useEffect(() => {
    const fetchAnnouncements = async (classRoomId) => {
      if (classRoomId) {
        setIsPageLoading(true)
        try {
          const announcements = await getAnnouncements(classRoomId)
          const processedAnnouncements = await processAnnouncements(announcements)
          setPosts(processedAnnouncements)
        } catch (error) {
          console.error("Error fetching announcements:", error)
        } finally {
          setIsPageLoading(false)
        }
      }
    }

    if (classData) {
      fetchAnnouncements(classData._id)
    }

    if (teacherData) {
      setCurrentUser({
        userId: teacherData._id,
        userName: teacherData.teacherName,
        userType: "teacher",
      })
    }
  }, [classData, teacherData])

  const handleDeletePost = async (postId) => {
    const deleteData = {
      body: {
        announcementId: postId,
      },
      request: "delete-announcement",
    }

    try {
      const deleteResponse = await postAws("https://api-dev.aurait.ai/announcement", "POST", deleteData)
      if (deleteResponse.statusCode === 201) {
        setPosts(posts.filter((post) => post._id !== postId))
      } else {
        console.error("Failed to delete post:", deleteResponse)
      }
    } catch (error) {
      console.error("Error deleting post:", error)
    }
  }

  const handlePostSubmit = async (e) => {
    e.preventDefault()

    if (!announcementDescription.trim() || !announcement_topic) return

    setIsLoading(true)

    let announcementAttach = ""
    try {
      if (announcementAttachment) {
        announcementAttach = await postTeacherAnnouncementFile(
          announcementAttachment,
          teacherData._id,
          classData._id,
          announcementAttachment.name,
        )
      }

      const announcementData = {
        body: {
          announcement_topic,
          classRoomId: classData._id,
          teacherId: classData.teacherId,
          announcementDescription,
          announcementAttachment: announcementAttachment ? announcementAttach.url : "",
        },
        request: "create-announcement",
      }

      const response = await postAws("https://api-dev.aurait.ai/announcement", "POST", announcementData)

      setAnnouncementTopic("general")
      setAnnouncementDescription("")
      setAnnouncementAttachment(null)
      setIsCreateAnnouncementOpen(false)

      // Add the new post to the existing posts
      setPosts([
        {
          _id: Date.now().toString(),
          teacherName: "You",
          createdAt: new Date().toISOString(),
          announcement_topic,
          announcementDescription,
          announcementAttachment: announcementAttachment ? announcementAttach.url : "",
          attachmentName: announcementAttachment ? announcementAttachment.name : "",
          announcementReact: [],
          announcementComments: [],
          isDisplayAttachment: false,
        },
        ...posts,
      ])
    } catch (err) {
      console.error("Error posting announcement:", err)
    } finally {
      setIsLoading(false)
    }
  }
const handleReact = async (postId) => {
  const reactData = {
    body: {
      announcementId: postId,
      userId: currentUser.userId,
      userName: currentUser.userName,
      userType: currentUser.userType,
    },
    request: "react-announcement",
  };

  try {
    const response = await postAws("https://api-dev.aurait.ai/announcement", "POST", reactData);

    const userAlreadyReacted = posts
      .find((post) => post._id === postId)
      ?.announcementReact.some((r) => r.userId === currentUser.userId);

    // Update UI based on toggle logic
    setPosts((prevPosts) =>
      prevPosts.map((post) => {
        if (post._id !== postId) return post;

        const updatedReact = userAlreadyReacted
          ? post.announcementReact.filter((r) => r.userId !== currentUser.userId) // remove reaction
          : [
              ...post.announcementReact,
              {
                userId: currentUser.userId,
                userName: currentUser.userName,
                userType: currentUser.userType,
              },
            ]; // add reaction

        return {
          ...post,
          announcementReact: updatedReact,
        };
      })
    );
  } catch (error) {
    console.error("Error toggling reaction:", error);
  }
};

  const handleCommentSubmit = async (postId) => {
    if (!newComments[postId]?.trim()) return

    const commentData = {
      body: {
        announcementId: postId,
        userComment: newComments[postId],
        userId: currentUser.userId,
        userName: currentUser.userName,
      },
      request: "comment-announcement",
    }

    try {
      const comment = await postAws("https://api-dev.aurait.ai/announcement", "POST", commentData)

      setPosts(
        posts.map((post) =>
          post._id === postId
            ? {
                ...post,
                announcementComments: [
                  ...post.announcementComments,
                  {
                    announcementId: postId,
                    userComment: newComments[postId],
                    userId: currentUser.userId,
                    userName: currentUser.userName,
                  },
                ],
              }
            : post,
        ),
      )
      setNewComments({ ...newComments, [postId]: "" })
    } catch (error) {
      console.error("Error submitting comment:", error)
    }
  }

  const toggleComments = (postId) => {
    setExpandedComments((prev) => {
      const newSet = new Set(prev)
      if (newSet.has(postId)) {
        newSet.delete(postId)
      } else {
        newSet.add(postId)
      }
      return newSet
    })
  }

  const getAnnouncementIcon = (type) => {
    const topic = announcement_topics.find((t) => t.value === type)
    return topic ? topic.icon : Megaphone
  }

  const handleFileChange = () => {
    const input = document.createElement("input")
    input.type = "file"
    input.accept = ".pdf,image/*"
    input.onchange = (e) => {
      const file = e.target.files[0]
      if (file && (file.type === "application/pdf" || file.type.startsWith("image/"))) {
        setAnnouncementAttachment(file)
      } else {
        alert("Please upload only PDF or image files.")
      }
    }
    input.click()
  }

  const renderAttachment = (attachmentUrl) => {
    if (!attachmentUrl) return null
    return (
      <iframe
        src={attachmentUrl}
        width="100%"
        height="500px"
        title="PDF Viewer"
        className="rounded-lg border border-gray-200"
      />
    )
  }

  const getInitials = (name) => {
    if (!name) return "U"
    return name
      .split(" ")
      .map((part) => part[0])
      .join("")
      .toUpperCase()
      .substring(0, 2)
  }

  const formatDate = (dateString) => {
    if (!dateString) return ""
    const date = new Date(dateString)
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    })
  }

  const formatTime = (dateString) => {
    if (!dateString) return ""
    const date = new Date(dateString)
    return date.toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
    })
  }

  if (isPageLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
        <div className="text-center">
          <div className="h-12 w-12 mx-auto mb-4">
            <Loader2 className="h-full w-full animate-spin text-blue-500" />
          </div>
          <p className="text-lg font-medium text-gray-700">Loading announcements...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="bg-gradient-to-br from-blue-50 to-indigo-100 min-h-screen p-4 md:p-6 -mt-5">
      <div className="max-w-3xl mx-auto">
        <div className="bg-white rounded-lg shadow-lg mb-6 overflow-hidden -mt-3">
          <button
            onClick={() => setIsCreateAnnouncementOpen(!isCreateAnnouncementOpen)}
            className="w-full flex items-center justify-between p-4 text-left font-bold text-lg hover:bg-blue-50 transition-all"
          >
            <span className="flex items-center">
              <Megaphone className="mr-2 h-5 w-5 text-blue-500" />
              Create New Announcement
            </span>
            <Plus
              className={`h-5 w-5 transform transition-transform duration-300 ${isCreateAnnouncementOpen ? "rotate-45" : ""}`}
            />
          </button>

          {isCreateAnnouncementOpen && (
            <div className="p-4 border-t">
              <form onSubmit={handlePostSubmit} className="space-y-4">
                <div className="space-y-2">
                  <label className="text-sm font-medium">Announcement Type</label>
                  <select
                    value={announcement_topic}
                    onChange={(e) => setAnnouncementTopic(e.target.value)}
                    className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    {announcement_topics.map((topic) => (
                      <option key={topic.value} value={topic.value}>
                        {topic.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-medium">Announcement Content</label>
                  <textarea
                    value={announcementDescription}
                    onChange={(e) => setAnnouncementDescription(e.target.value)}
                    placeholder="Write your announcement here..."
                    className="w-full min-h-[120px] p-2 border border-gray-300 rounded-md resize-none focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    required
                  />
                </div>

                <div className="flex flex-col sm:flex-row justify-between items-center gap-3">
                  <button
                    type="button"
                    onClick={handleFileChange}
                    className="w-full sm:w-auto px-4 py-2 border border-gray-300 rounded-md flex items-center justify-center hover:bg-gray-50 transition-colors"
                  >
                    <Paperclip className="mr-2 h-4 w-4" />
                    {announcementAttachment ? "Change Attachment" : "Add Attachment"}
                  </button>
                  <button
                    type="submit"
                    disabled={isLoading}
                    className="w-full sm:w-auto px-4 py-2 bg-blue-500 text-white rounded-md flex items-center justify-center hover:bg-blue-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isLoading ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Posting...
                      </>
                    ) : (
                      <>
                        <Send className="mr-2 h-4 w-4" />
                        Post Announcement
                      </>
                    )}
                  </button>
                </div>

                {announcementAttachment && (
                  <div className="flex items-center p-2 bg-blue-50 rounded-md text-sm">
                    <Paperclip className="h-4 w-4 text-blue-500 mr-2" />
                    <span className="text-blue-700 font-medium truncate flex-1">{announcementAttachment.name}</span>
                    <button
                      type="button"
                      className="h-6 w-6 p-0 text-gray-500 hover:text-red-500 focus:outline-none"
                      onClick={() => setAnnouncementAttachment(null)}
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                )}
              </form>
            </div>
          )}
        </div>

        {posts.length === 0 ? (
          <div className="text-center p-12 bg-white rounded-lg shadow-md">
            <Megaphone className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-700 mb-2">No announcements yet</h3>
            <p className="text-gray-500">Create your first announcement to get started.</p>
          </div>
        ) : (
          <div className="space-y-6">
            {posts.map((post) => (
              <div key={post._id} className="bg-white rounded-lg shadow-md overflow-hidden">
                <div className="p-4 pb-2">
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-3">
                      <div className="h-10 w-10 rounded-full bg-blue-100 flex items-center justify-center text-blue-600 font-medium">
                        {getInitials(post.teacherName)}
                      </div>
                      <div>
                        <h3 className="font-semibold text-base">{post.teacherName}</h3>
                        <div className="text-xs text-gray-500 flex flex-wrap gap-2">
                          <span className="flex items-center">
                            <Calendar size={12} className="mr-1" />
                            {formatDate(post.createdAt)}
                          </span>
                          <span className="flex items-center">
                            <Clock size={12} className="mr-1" />
                            {formatTime(post.createdAt)}
                          </span>
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                            {React.createElement(getAnnouncementIcon(post.announcement_topic), { size: 12 })}
                            <span className="capitalize">{post.announcement_topic}</span>
                          </span>
                        </div>
                      </div>
                    </div>
                    <button
                      className="h-8 w-8 flex items-center justify-center text-red-500 hover:text-red-700 hover:bg-red-50 rounded-full"
                      onClick={() => handleDeletePost(post._id)}
                      title="Delete announcement"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>

                  <div className="mt-4">
                    <p className="text-gray-700 whitespace-pre-line mb-3">{post.announcementDescription}</p>

                    {post.announcementAttachment && (
                      <button
                        className="mb-3 px-3 py-1.5 text-sm border border-blue-200 rounded-md text-blue-600 hover:text-blue-800 hover:bg-blue-50 flex items-center"
                        onClick={() => handleDisplayAttachment(post._id)}
                      >
                        <Paperclip size={14} className="mr-2" />
                        <span>{post.attachmentName}</span>
                      </button>
                    )}
                  </div>

                  {post.isDisplayAttachment && (
                    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
                      <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] flex flex-col">
                        <div className="flex justify-between items-center p-4 border-b">
                          <h3 className="font-medium">Attachment: {post.attachmentName}</h3>
                          <button
                            className="h-8 w-8 flex items-center justify-center rounded-full hover:bg-gray-100"
                            onClick={() => handleDisplayAttachment(post._id)}
                          >
                            <X size={18} />
                          </button>
                        </div>
                        <div className="flex-1 overflow-auto p-4">{renderAttachment(post.attachmentPresignedUrl)}</div>
                      </div>
                    </div>
                  )}
                </div>

                <div className="border-t border-b flex">
                  <button
                    className={`flex-1 py-3 flex items-center justify-center ${
                      post.announcementReact.some((r) => r.userId === currentUser.userId)
                        ? "text-blue-600"
                        : "text-gray-600"
                    } hover:bg-gray-50`}
                    onClick={() => handleReact(post._id)}
                  >
                    <ThumbsUp size={16} className="mr-2" />
                    <span>
                      {post.announcementReact.length} {post.announcementReact.length === 1 ? "Like" : "Likes"}
                    </span>
                  </button>

                  <button
                    className="flex-1 py-3 flex items-center justify-center text-gray-600 hover:bg-gray-50"
                    onClick={() => toggleComments(post._id)}
                  >
                    <MessageCircle size={16} className="mr-2" />
                    <span>
                      {post.announcementComments.length}{" "}
                      {post.announcementComments.length === 1 ? "Comment" : "Comments"}
                    </span>
                    {expandedComments.has(post._id) ? (
                      <ChevronUp size={16} className="ml-1" />
                    ) : (
                      <ChevronDown size={16} className="ml-1" />
                    )}
                  </button>
                </div>

                {expandedComments.has(post._id) && (
                  <div className="p-4 space-y-4">
                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={newComments[post._id] || ""}
                        onChange={(e) => setNewComments({ ...newComments, [post._id]: e.target.value })}
                        placeholder="Write a comment..."
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                      <button
                        onClick={() => handleCommentSubmit(post._id)}
                        disabled={!newComments[post._id]?.trim()}
                        className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Comment
                      </button>
                    </div>

                    {post.announcementComments.length > 0 ? (
                      <div className="space-y-3 max-h-[300px] overflow-y-auto">
                        {post.announcementComments.map((comment, idx) => (
                          <div key={idx} className="bg-gray-50 p-3 rounded-lg">
                            <div className="flex items-center gap-2 mb-1">
                              <div className="h-6 w-6 rounded-full bg-blue-100 flex items-center justify-center text-blue-600 text-xs font-medium">
                                {getInitials(comment.userName)}
                              </div>
                              <span className="font-medium text-sm">{comment.userName}</span>
                            </div>
                            <p className="text-gray-700 text-sm pl-8">{comment.userComment}</p>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-center text-gray-500 text-sm py-2">
                        No comments yet. Be the first to comment!
                      </p>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

